import { Button } from 'components/ui'
import { useBuilderWidget } from 'components/TasksV2/TaskCreation/contexts/BuilderWidgetContext'
import { useCreationText } from 'components/TasksV2/TaskCreation/hooks/useCreationText'
// PollOptions.tsx
import React, { useEffect, useRef } from 'react'
import { Flex, Divider, Box, Text } from '@chakra-ui/core'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { TaskInput } from 'components/TasksV2/UI/TaskInput'
import { RemoveButton } from 'components/ui/Button/RemoveButton'
import useLocaleText from 'components/useLocaleText'
import { Plus } from 'lucide-react'

interface PollOptionsProps {
  optionsPath: string
  headerAction?: React.ReactNode
}

const PollOptions: React.FC<PollOptionsProps> = ({ optionsPath, headerAction }) => {
  const builder = useBuilderWidget()
  const t = useCreationText()

  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const t_optionPlaceholder = useLocaleText('t_optionPlaceholder')
  const t_optionLabel = useLocaleText('t_optionLabel')
  const optionRequired = useLocaleText('t_poll_st_error')
  const deleteOption = useLocaleText('delete')

  const { control, register } = useFormContext()

  const {
    fields: optionFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: optionsPath,
    keyName: 'id',
  })

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === 'Enter' && index === optionFields.length - 1) {
      event.preventDefault()
      append({ value: '' })
      setTimeout(() => inputRefs.current[index + 1]?.focus(), 0)
    }
  }

  const handleRemove = (index: number) => {
    if (optionFields.length <= 2) {
      return
    }

    remove(index)
  }

  useEffect(() => {
    if (optionFields.length < 2) {
      const defaultOptions = ['', '']
      const initializeOptions = async () => {
        for (const option of defaultOptions) {
          await append({ value: option })
        }
      }
      initializeOptions()
    }
    // eslint-disable-next-line
  }, [])

  return (
    <Box className={builder ? 'creation-options-section' : undefined} my={builder ? '16px' : '20px'}>
      <div className={builder ? 'creation-answer-heading' : undefined}>
        <Text fontWeight="medium" px="15px" fontSize="14px">
          {builder ? (
            <>
              {t.answerOptions} <span className="creation-required-star">*</span>
            </>
          ) : (
            t_optionLabel
          )}
        </Text>
        {headerAction}
      </div>
      <Flex
        className={builder ? 'creation-options-body' : undefined}
        direction="column"
        border={builder ? 'none' : '2px solid #f0f0f0'}
        borderRadius="10px"
        mt="5px"
        p="10px"
      >
        {optionFields.map(({ id, value }, optIndex) => {
          const isLastOption = optIndex === optionFields.length - 1

          return (
            <React.Fragment key={id}>
              <Flex className={builder ? 'creation-option-row' : undefined} key={id} align="center" w="100%">
                {builder && <span className="creation-option-number">{optIndex + 1}</span>}
                <TaskInput
                  ref={(el: HTMLInputElement) => {
                    inputRefs.current[optIndex] = el
                    if (el) {
                      register(el, { required: optionRequired })
                    }
                  }}
                  name={`${optionsPath}[${optIndex}].value`}
                  aria-label={`${builder ? t.option : t_optionPlaceholder} ${optIndex + 1}`}
                  defaultValue={value}
                  placeholder={`${builder ? t.option : t_optionPlaceholder} ${optIndex + 1}`}
                  border="none"
                  boxShadow="none"
                  borderRadius="none"
                  px="5px"
                  height={builder ? '36px' : undefined}
                  width="100%"
                  onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(event, optIndex)}
                  inputElement={
                    !builder && (
                      <RemoveButton
                        top="9px"
                        isDisabled={optionFields.length <= 2}
                        onClick={() => handleRemove(optIndex)}
                        aria-label={deleteOption}
                      />
                    )
                  }
                />
                {builder && (
                  <RemoveButton
                    className="creation-remove-option"
                    type="button"
                    top="50%"
                    transform="translateY(-50%)"
                    isDisabled={optionFields.length <= 2}
                    onClick={() => handleRemove(optIndex)}
                    aria-label={deleteOption}
                  />
                )}
              </Flex>
              {!builder && !isLastOption && <Divider my={0} />}
            </React.Fragment>
          )
        })}
        {builder ? (
          <Button
            variant="secondary"
            height="32px"
            type="button"
            className="creation-add-option"
            alignSelf="center"
            width="auto"
            padding="0 16px"
            fontSize="12px"
            style={{ gap: '8px' }}
            onClick={() => append({ value: '' })}
          >
            <Plus size={14} />
            {t.addOption}
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            aria-label={t.addOption}
            onClick={() => append({ value: '' })}
            px="15px"
            justifyContent="center"
          >
            <Plus size={16} />
          </Button>
        )}
      </Flex>
    </Box>
  )
}
export default PollOptions
